import { Injectable, OnDestroy } from "@angular/core";
import { DynamicFormConfig, DynamicFormReadyEvent, SelectItem } from "@mydocma/dynamic-form";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, debounceTime, first, firstValueFrom } from "rxjs";
import { FooterHeaderFieldEnum, footerHeaderFormConfig } from "../steps/report-options-step/config/header-footer-form.config";
import {copy} from "@mydocma/common/src/lib/deep";
import { OptionsFieldsEnum, optionsFormConfig, optionsOrientation } from "../steps/report-options-step/config/options-form.config";
import { MmLayoutStateService } from "projects/mydocma/mm/src/lib/mm-filter/layout/layout-state.service";
import { Step } from "@mydocma/import-export";
import { PersonalizationService } from "@mydocma/app-container";
import { TranslateService } from "@ngx-translate/core";
import { MmProjectService } from "projects/mydocma/mm/src/lib/mm-project/mm-project.service";
import { FaultReportConfig, ReportOptions } from "../../faults-report.config";
import { AttachmentFieldEnum, attachmentFormConfig } from "../steps/report-options-step/config/attachment-form.config";

@UntilDestroy()
@Injectable()
export class ReportOptionsService implements OnDestroy {

  public footerFormServices: DynamicFormReadyEvent;
  public optionsFormServices: DynamicFormReadyEvent;
  public attachmentFormServices: DynamicFormReadyEvent;

  public footerFormConfig$ = new BehaviorSubject<DynamicFormConfig>(null);
  public optionsFormConfig$ = new BehaviorSubject<DynamicFormConfig>(null);
  public attachmentFormConfig$ = new BehaviorSubject<DynamicFormConfig>(null);

  public step: Step;
  public reportConfig: FaultReportConfig;

  constructor(
    public readonly layoutService: MmLayoutStateService,
    private readonly projectService: MmProjectService,
    private readonly personalizationService: PersonalizationService,
    private readonly translateService: TranslateService) {
  }

  ngOnDestroy(): void {
  }

  public attachToHeaderFooterForm(event: DynamicFormReadyEvent) {
    this.footerFormServices = event;
    this.footerFormServices.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this), debounceTime(250))
      .subscribe(async (f: any) => {
        await this.saveOptions();
        this.reportConfig.previewModel = null;
      });
  }

  public attachToOptionsForm(event: DynamicFormReadyEvent) {
    this.optionsFormServices = event;

    this.optionsFormServices.dynamicFormService.updateFieldConfig({
      name: optionsOrientation.name,
      source: this.getOrientationSource(),
    } as any);

    this.optionsFormServices.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe(async (f: any) => {
        await this.saveOptions();
        this.reportConfig.previewModel = null;
      }
     );
  }

  public attachToAttachmentsForm(event: DynamicFormReadyEvent) {
    this.attachmentFormServices = event;
    this.attachmentFormServices.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe(async (f: any) => {
        await this.saveOptions();
        this.reportConfig.previewModel = null;
      }
     );
  }

  public async createForms(): Promise<void> {
    const project = await this.projectService.currentProject.pipe(first()).toPromise();
    this.step.completed = true;

    await this.createOptionsFormConfig();
    await this.createFooterHeaderFormConfig();
    await this.createAttachmentFormConfig();
  }


  private async saveOptions() {
    const templateId = this.reportConfig.selectedTemplate.Id;
    const keyLastOptions = `mm.<DefectReport>`;
    const lastOptionsData = {...(await this.personalizationService.getValue(keyLastOptions) as any)};

    const headerFooterFormGroup = this.footerFormServices.dynamicFormService.formGroup;


    // Save the selected title for the current template
    const title = headerFooterFormGroup.get(FooterHeaderFieldEnum.header).value;
    const titles = lastOptionsData?.titles ?? [];
    const existingTitle = titles.find(title => title.templateId === templateId);
    const newTitle = {templateId, title};

    if (existingTitle) {
      existingTitle.title = title;
    } else {
      titles.push(newTitle);
    }

    // Save the other selected options
    const value = {
      [FooterHeaderFieldEnum.footerLeft]: headerFooterFormGroup.get(FooterHeaderFieldEnum.footerLeft).value,
      [FooterHeaderFieldEnum.footerCenter]: headerFooterFormGroup.get(FooterHeaderFieldEnum.footerCenter).value,
      [FooterHeaderFieldEnum.date]: headerFooterFormGroup.get(FooterHeaderFieldEnum.date).value,
      [FooterHeaderFieldEnum.projectNumber]: headerFooterFormGroup.get(FooterHeaderFieldEnum.projectNumber).value,
      [FooterHeaderFieldEnum.projectTitle]: headerFooterFormGroup.get(FooterHeaderFieldEnum.projectTitle).value,
      [FooterHeaderFieldEnum.user]: headerFooterFormGroup.get(FooterHeaderFieldEnum.user).value,
    };


    let options;
    if(this.optionsFormServices?.dynamicFormService) {
      const optionsFormGroup = this.optionsFormServices.dynamicFormService.formGroup;
      options = {
        [OptionsFieldsEnum.columnSize]: optionsFormGroup.get(OptionsFieldsEnum.columnSize).value,
        [OptionsFieldsEnum.orientation]: optionsFormGroup.get(OptionsFieldsEnum.orientation).value,
        [OptionsFieldsEnum.printResolution]: optionsFormGroup.get(OptionsFieldsEnum.printResolution).value,
        [OptionsFieldsEnum.paperFormat]: optionsFormGroup.get(OptionsFieldsEnum.paperFormat).value,
      }
    }


    let attachments;
    if(this.attachmentFormServices?.dynamicFormService) {
      const attachmentsFormGroup = this.attachmentFormServices.dynamicFormService.formGroup;
      attachments = {
        [AttachmentFieldEnum.comment]: attachmentsFormGroup.get(AttachmentFieldEnum.comment).value,
        [AttachmentFieldEnum.fileName]: attachmentsFormGroup.get(AttachmentFieldEnum.fileName).value,
        [AttachmentFieldEnum.imageDate]: attachmentsFormGroup.get(AttachmentFieldEnum.imageDate).value,
      }
    }

    // Save the options to persistent storage
    try {
      await this.personalizationService.setValue(keyLastOptions, {...lastOptionsData, titles, ...value,...attachments, ...options});
    } catch (error) {
      console.error(`An error occurred while saving report options: ${error}`);
    }
  }

  public getOptionsValue(): any {
    if (!this.reportConfig?.selectedTemplate) {
      throw new Error('Missing report configuration');
    }

    const options = {} as ReportOptions;

    const footerHeaderFormGroup = this.footerFormServices?.dynamicFormService?.formGroup;
    if (footerHeaderFormGroup) {
      options.footerLeft = footerHeaderFormGroup.get(FooterHeaderFieldEnum.footerLeft)?.value;
      options.footerRight = footerHeaderFormGroup.get(FooterHeaderFieldEnum.footerCenter)?.value;
      options.headline = footerHeaderFormGroup.get(FooterHeaderFieldEnum.header)?.value;
      options.showFooterDate = footerHeaderFormGroup.get(FooterHeaderFieldEnum.date)?.value;
      options.showProjectNumber = footerHeaderFormGroup.get(FooterHeaderFieldEnum.projectNumber)?.value;
      options.showProjectTitle = footerHeaderFormGroup.get(FooterHeaderFieldEnum.projectTitle)?.value;
      options.showFooterUser = footerHeaderFormGroup.get(FooterHeaderFieldEnum.user)?.value;
    }

    const optionsFormGroup = this.optionsFormServices?.dynamicFormService?.formGroup;
    if (optionsFormGroup) {
      options.columnSize = optionsFormGroup.get(OptionsFieldsEnum.columnSize)?.value;
      options.orientation = optionsFormGroup.get(OptionsFieldsEnum.orientation)?.value;
      options.printResolution = optionsFormGroup.get(OptionsFieldsEnum.printResolution)?.value;
      options.paperFormat = optionsFormGroup.get(OptionsFieldsEnum.paperFormat)?.value;
    }

    const attachmentFormGroup = this.attachmentFormServices?.dynamicFormService?.formGroup;
    if (attachmentFormGroup) {
      options.comment = attachmentFormGroup.get(AttachmentFieldEnum.comment)?.value;
      options.fileName = attachmentFormGroup.get(AttachmentFieldEnum.fileName)?.value;
      options.imageDate = attachmentFormGroup.get(AttachmentFieldEnum.imageDate)?.value;
    }

    return options;
  }

  private async createFooterHeaderFormConfig(): Promise<void> {
    const formConfig: DynamicFormConfig = copy(footerHeaderFormConfig) as any;
    const layoutState = await  firstValueFrom(this.layoutService.currentLayoutState$.pipe(first()));

    const templateId = this.reportConfig.selectedTemplate.Id;
    const keyLastOptions = `mm.<DefectReport>`;
    try {
      const personalisationData = await this.personalizationService.getValue(keyLastOptions) as any;

      if(personalisationData) {
        const title = personalisationData?.titles?.find(title => title.templateId === templateId)?.title
                            || this.reportConfig.selectedTemplate.Name1;

        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.header).value = title || layoutState.state.layout.Name;
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.footerLeft).value = personalisationData?.[FooterHeaderFieldEnum.footerLeft];
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.footerCenter).value = personalisationData?.[FooterHeaderFieldEnum.footerCenter];
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.date).value = personalisationData?.[FooterHeaderFieldEnum.date];
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.projectNumber).value = personalisationData?.[FooterHeaderFieldEnum.projectNumber];
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.projectTitle).value = personalisationData?.[FooterHeaderFieldEnum.projectTitle];
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.user).value = personalisationData?.[FooterHeaderFieldEnum.user];
      } else {
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.header).value = layoutState.state.layout.Name;
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.footerLeft).value = '';
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.footerCenter).value = '';
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.date).value = true;
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.projectNumber).value = true;
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.projectTitle).value = true;
        formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.user).value = true;
      }

    } catch (error) {
      console.error(`An error occurred while getting personalization data: ${error}`);
    }

    if(!this.reportConfig.selectedTemplate.Id) {
      formConfig.fields.find(p => p.id === FooterHeaderFieldEnum.user).hidden = true;
    }

    this.footerFormConfig$.next(formConfig);
  }

  private async createOptionsFormConfig(): Promise<void> {
    const formConfig: DynamicFormConfig = copy(optionsFormConfig) as any;
    const keyLastOptions = `mm.<DefectReport>`;

    try {
      const personalisationData = await this.personalizationService.getValue(keyLastOptions) as any;
      const columnSizeField = personalisationData?.[OptionsFieldsEnum.columnSize];
      const orientationField = personalisationData?.[OptionsFieldsEnum.orientation];
      const resolutionField = personalisationData?.[OptionsFieldsEnum.printResolution];
      const printerField = personalisationData?.[OptionsFieldsEnum.paperFormat];

      if(personalisationData == null) {
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.columnSize).value = true;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.printResolution).value = true;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.orientation).value = formConfig.fields.find(p => p.id === OptionsFieldsEnum.orientation).value;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.paperFormat).value = formConfig.fields.find(p => p.id === OptionsFieldsEnum.paperFormat).value;
      } else {
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.columnSize).value =  !!columnSizeField;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.printResolution).value = !!resolutionField;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.orientation).value = orientationField;
        formConfig.fields.find(p => p.id === OptionsFieldsEnum.paperFormat).value = printerField;
      }

    } catch(error) {
      console.error(`An error occurred while getting personalization data: ${error}`);
    }

    this.optionsFormConfig$.next(formConfig);
  }

  private async createAttachmentFormConfig(): Promise<void> {
    const formConfig: DynamicFormConfig = copy(attachmentFormConfig) as any;
    const keyLastOptions = `mm.<DefectReport>`;

    try {
      const personalisationData = await this.personalizationService.getValue(keyLastOptions) as any;
      const commentSizeField = personalisationData?.[AttachmentFieldEnum.comment];
      const fileNameField = personalisationData?.[AttachmentFieldEnum.fileName];
      const imageDateField = personalisationData?.[AttachmentFieldEnum.imageDate];

      if(personalisationData == null) {
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.comment).value = true;
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.fileName).value = true;
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.imageDate).value = true;
      } else {
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.comment).value = !!commentSizeField;
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.fileName).value = !!fileNameField;
        formConfig.fields.find(p => p.id === AttachmentFieldEnum.imageDate).value = !!imageDateField;
      }
    } catch(error) {
      console.error(`An error occurred while getting personalization data: ${error}`);
    }

    this.attachmentFormConfig$.next(formConfig);
  }

  private getOrientationSource(): SelectItem[] {
    return [{
      id: 1,
      text: this.translateService.instant('MM.report.tabs.options.portrait')
    }, {
      id: 2,
      text: this.translateService.instant('MM.report.tabs.options.landscape')
    }]
  }

}
